/**
 * Whether this build routes a sign-in to the environment the account actually lives on
 * (PHX-1295, design §3.6).
 *
 * `app.caresimple.com` is usa01's portal and the shared front door for everyone without a health
 * system of their own, so only that build resolves; every other environment is a destination and
 * leaves the flag unset. Absent means off, so an environment that has not been told stays inert
 * rather than redirecting its users somewhere.
 *
 * Build-time, not runtime: Vite inlines `VITE_*` at build, so this must be in the environment's
 * `frontend-<env>` Key Vault secret (the base64 `.env` the pipeline decodes) before the image is
 * built. A Kubernetes env var or a pod restart cannot turn it on, which is exactly how
 * `VITE_DTP_SELF_SIGNUP_ENABLED` shipped silently closed.
 */
export const LOGIN_ROUTER_ENABLED = import.meta.env.VITE_LOGIN_ROUTER_ENABLED === 'true';

/**
 * Resolution goes through **our own API**, not the RPM9000 directly: same origin, so no CORS, and
 * the estate's identity service never has to face a browser. health-api holds that conversation
 * server-to-server and rate-limits it. Nothing to configure — this is the gateway path the app
 * already proxies.
 */
export const LOGIN_RESOLVER_PATH = '/api/clinician-gateway/identity/resolve-login';

/**
 * The environment that owns self-serve signup, as an absolute URL (design §3.5).
 *
 * The front door shows the signup CTA like every other environment, but clicking it leaves for DTP
 * rather than opening a wizard here: an account that does not exist yet belongs nowhere, so there is
 * nothing to resolve and the destination is static. DTP itself sets `VITE_DTP_SELF_SIGNUP_ENABLED`
 * instead and keeps the CTA local.
 *
 * Deliberately a `VITE_` value and not a code constant: which environment is DTP differs between the
 * QA rehearsal and production, so it is one of the few genuinely per-estate facts. Absent means the
 * CTA is not shown, matching how both other flags fail closed — so on the router this must be set
 * alongside `VITE_LOGIN_ROUTER_ENABLED`, and the runbook lists them together.
 */
export const DTP_SIGNUP_URL = import.meta.env.VITE_DTP_SIGNUP_URL ?? '';
